import { useEffect, useMemo, useRef, useState } from 'react';
import { endpoints } from '../../api';
import type { HistoryOrder } from '../../api/history';
import type { UserAvatar } from '../../api/people';
import type { UserVoice } from '../../api/user-voices';
import { uiFormat, uiText } from '../../i18n';
import { PromptChip } from '../../ui/PromptChip';
import { TextEditor } from '../../ui/TextEditor';
import { TextField } from '../../ui/TextField';
import { sampleFile } from '../clone/sample-file';
import { StudioCreateButton, StudioSettings, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { StudioHeaderContent } from '../studio/StudioHeaderContent';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioSheet } from '../studio/StudioSheet';
import { studioExpectedPrice, type StudioApp } from '../studio/studio-api';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { useStudioForm } from '../studio/use-studio-form';
import { SpeechAudioInput } from '../tts/SpeechAudioInput';
import { VoicePick } from '../tts/VoicePick';
import { patchSpec, readText, specKey as keyInfo } from '../reels/form-keys';
import { useScreenUpload } from '../reels/use-screen-upload';
import { CoverOrderResult } from './CoverOrderResult';
import { CoverStyles } from './CoverStyles';
import { coverMissing, coverPolicy, coverSupported, mmss, OWN_STYLE, songSlot, songValue, type CoverPolicy } from './cover-policy';
import '../studio/native-template.css';
import './cover-native.css';

interface Song {
  owner?: number;
  uploadId?: string;
  file: File;
  ms: number | null;
}

/** «Создать кавер» (APP-SPEC-5 §9): песня (своя запись, файл или «Мои треки»), фрагмент, стиль или «Свой стиль»,
    карточка «Ещё настройки» (голос, длительность, пожелания к стилю), кнопка с ценой и хвост с двумя вариантами.
    Форма приходит с сервера; пока её нет (стенд отвечает отказом), экран показывает недоступность и повтор. */
export function CoverOrderForm({
  app,
  avatar,
  initialInputs,
  headerActive = true,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form } = state;
  const supported = !!form && coverSupported(form);
  const policy = form && supported ? coverPolicy(form) : null;
  const durationId = readText(keyInfo(form, 'duration'), state.inputs);
  const duration = policy?.durations.find(d => d.id === durationId) ?? policy?.durations.find(d => d.id === 'frag') ?? policy?.durations[0];
  // На кнопке — сумма, которая уйдёт в заказ (state.price); без списания цена фрагмента его не меняет.
  const price = form?.chargeable === false ? state.price : (duration?.price ?? state.price);
  const unit = !duration ? '' : duration.id === 'full' ? uiFormat('{0} C за песню', price) : uiFormat('{0} C за фрагмент {1}', price, mmss(duration.windowMs));
  return (
    <section className="native-studio-form server-cover cv-in">
      <StudioHeaderContent enabled={headerActive}>
        <div className="bc-tt">
          <h2>{uiText('Создать кавер')}</h2>
          {unit && !state.orderId && <small className="u">{unit}</small>}
        </div>
      </StudioHeaderContent>
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <div className="st-zero cv-unavailable" role="alert">
          <StudioIcon kind="music" />
          <h3>{uiText('Создать кавер сейчас нельзя')}</h3>
          <p>{uiText(state.error)}</p>
          <button type="button" className="btn line" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </div>
      )}
      {form && !supported && !state.orderId && (
        <div className="st-zero cv-unavailable" role="status">
          <StudioIcon kind="music" />
          <h3>{uiText('Создание кавера пока недоступно')}</h3>
          <p>{uiText('Форма инструмента ещё не опубликована полностью. Загляните позже.')}</p>
        </div>
      )}
      {form && policy && !state.orderId && <CoverForm state={state} policy={policy} price={price} />}
      {state.orderId && <CoverOrderResult state={state} />}
    </section>
  );
}

function CoverForm({ state, policy, price }: { state: CircleFormState; policy: CoverPolicy; price: number }) {
  const form = state.form!;
  const { inputs, locked } = state;
  const song = songSlot(form)!;
  const direct = useScreenUpload();
  const info = (key: string) => keyInfo(form, key);
  const set = (patch: Record<string, string | number | boolean | string[] | null>) => state.setInputs(current => patchSpec(form, current, patch));
  const clearSong = () => (song.screen ? set({ song_upload_id: null }) : state.clearUpload(song.key));
  const own = readText(info('own_style'), inputs) === 'true';
  const styleId = readText(info('style_id'), inputs);
  const styleText = readText(info('style_text'), inputs);
  const sourceId = readText(info('source_order_id'), inputs);
  const durationId = readText(info('duration'), inputs) || 'frag';
  const duration = policy.durations.find(d => d.id === durationId) ?? policy.durations[0];
  const fromMs = Number(readText(info('from_ms'), inputs)) || 0;
  const voiceMode = readText(info('voice_mode'), inputs) || 'src';
  const [local, setLocal] = useState<Song | null>(null);
  const [songError, setSongError] = useState('');
  const [mine, setMine] = useState<{ open: boolean; items: HistoryOrder[] | null; error: boolean }>({ open: false, items: null, error: false });
  const [editor, setEditor] = useState(false);
  const [clone, setClone] = useState<UserVoice | null>(null);
  const [mineTitle, setMineTitle] = useState('');
  const owner = state.ownerId;
  // Локальный файл — тот, что сейчас загружается (без id), или тот, чей id стоит во входах.
  const uploaded = songValue(form, inputs);
  const shown = local && local.owner === owner && (!local.uploadId || local.uploadId === uploaded) ? local : null;
  // Умолчания экрана, если форма публикует ключи: фрагмент 1:00 и «Оригинал».
  useEffect(() => {
    state.setInputs(current =>
      patchSpec(form, current, {
        ...(info('duration') && !readText(info('duration'), current) && policy.durations.some(d => d.id === 'frag') ? { duration: 'frag' } : {}),
        ...(info('voice_mode') && !readText(info('voice_mode'), current) ? { voice_mode: 'src' } : {}),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  // Песня повтора без локального файла: длительность читаем из её превью.
  useEffect(() => {
    if (!uploaded || !owner || local) return;
    const request = new AbortController();
    void (async () => {
      const blob = await endpoints.uploadPreview(uploaded, request.signal);
      const next = new File([blob], uiText('Песня'), { type: blob.type });
      const ms = await sampleFile.readDurationMs(next, request.signal);
      if (!request.signal.aborted) setLocal({ owner, uploadId: uploaded, file: next, ms });
    })().catch(() => {
      if (!request.signal.aborted) setSongError('Не удалось прочитать песню. Загрузите её заново.');
    });
    return () => request.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploaded, owner, !!local]);
  // «Мой голос · клон»: первый готовый свой голос с переозвучкой.
  useEffect(() => {
    if (!info('voice_mode')) return;
    let active = true;
    void endpoints.userVoices
      .list()
      .then(page => {
        if (active) setClone(page.items.find(v => v.status === 'ready' && v.is_s2s && v.voice_id) ?? null);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);
  const choose = async (next: File) => {
    setSongError('');
    let ms: number;
    try {
      ms = await sampleFile.readDurationMs(next);
    } catch {
      setSongError('Не удалось прочитать песню.');
      return;
    }
    if (ms < policy.minMs) return setSongError('Песня короче 6 секунд');
    if (ms > policy.maxMs) return setSongError(uiFormat('Песня длиннее {0} минут', Math.round(policy.maxMs / 60000)));
    clearSong();
    setLocal({ owner, file: next, ms });
    set({ source_order_id: null, from_ms: null });
    if (song.screen) {
      const id = await direct.upload(next, 'audio');
      if (id) {
        setLocal(current => (current?.file === next ? { ...current, uploadId: id } : current));
        set({ song_upload_id: id });
      } else setSongError('Загрузка не подтверждена. Проверьте её кнопкой ниже.');
    } else if (!(await state.upload(song.key, next))) setSongError('Загрузка не подтверждена. Проверьте её кнопкой ниже.');
  };
  const songMs = sourceId ? null : (shown?.ms ?? null);
  const missing = coverMissing(form, policy, inputs, state.rights, { songMs, own });
  // Цена выбора сверяется с суммой заказа; без списания сверять нечего — сервер всё равно проверит expected_price.
  const blocker = missing ?? (form.chargeable !== false && price !== state.price ? uiText('Цена этого выбора пока не подтверждена') : null);
  const windowMs = duration?.windowMs ?? 60000;
  const maxFrom = songMs !== null ? Math.max(0, songMs - windowMs) : 0;
  const audio = useRef<HTMLAudioElement>(null);
  const shownFile = shown?.file;
  const url = useMemo(() => (shownFile ? URL.createObjectURL(shownFile) : ''), [shownFile]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  const listen = () => {
    const element = audio.current;
    if (!element) return;
    if (!element.paused) return element.pause();
    element.currentTime = duration?.id === 'full' ? 0 : fromMs / 1000;
    void element.play().catch(() => {});
  };
  const chips = own ? policy.ownChips : policy.addChips;
  const textInfo = info('style_text');
  const voiceSlot = voiceMode === 'voice' ? (state.voice?.name ?? clone?.name ?? uiText('Свой голос')) : uiText('Оригинал');
  const slots: StudioSettingSlot[] = [
    ...(info('voice_mode') ? [{ title: 'Голос', value: voiceSlot, section: 'voice' }] : []),
    ...(info('duration') && policy.durations.length ? [{ title: 'Длительность', value: duration?.id === 'full' ? 'Вся песня' : uiFormat('{0} минута', Math.round(windowMs / 60000)), section: 'duration' }] : []),
    ...(textInfo && !own ? [{ title: 'Пожелания к стилю', value: styleText, section: 'wish', kind: 'text' as const, empty: !styleText.trim(), wide: true }] : []),
  ];
  const toggleChip = (chip: string) => {
    if (own) return set({ style_text: styleText === chip ? null : chip });
    const parts = styleText.split(',').map(x => x.trim()).filter(Boolean);
    const next = parts.includes(chip) ? parts.filter(x => x !== chip) : [...parts, chip];
    const value = next.join(', ');
    if (Array.from(value).length > policy.styleMax) return;
    set({ style_text: value || null });
  };
  const textField = (label: string) => (
    <TextField
      label={uiText(label)}
      value={styleText}
      limit={policy.styleMax}
      minimum={own ? policy.styleMin : 0}
      disabled={locked}
      onChange={value => set({ style_text: value || null })}
      onExpand={() => setEditor(true)}
      expandLabel={uiText('Во весь экран')}
    >
      {chips.length > 0 && (
        <div className="chips">
          {chips.map(chip => (
            <PromptChip key={chip} label={chip} selected={own ? styleText === chip : styleText.split(',').map(x => x.trim()).includes(chip)} disabled={locked} onClick={() => toggleChip(chip)} />
          ))}
        </div>
      )}
    </TextField>
  );
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        if (!state.busy && !state.submissionBlocked && (state.intent || (form.available && !blocker))) void state.submit();
      }}
    >
      <div className="cv-up studio-native-upload">
        <div className={`bcu${shown || sourceId ? ' has' : ''}`}>
          {!shown && !sourceId && (
            <div className="bcu-empty">
              <span className="ic">
                <StudioIcon kind="music" />
              </span>
              <b>{uiText('Загрузите песню')}</b>
              <p>{uiText('Свою или ту, на которую у вас есть права')}</p>
            </div>
          )}
          <label className="photo-consent">
            <input
              type="checkbox"
              checked={state.rights}
              disabled={locked}
              onChange={event => {
                state.setRights(event.target.checked);
                set({ rights_confirmed: event.target.checked || null });
              }}
            />
            {uiText('Только свои песни или песни с правами: подтверждаю права на эту запись')}
          </label>
          {!sourceId && (
            <SpeechAudioInput
              key={owner ?? 'owner'}
              file={shown?.file ?? null}
              disabled={locked || !state.rights}
              accept={song.accept.length ? song.accept : ['audio/mpeg', 'audio/wav', 'audio/mp4']}
              maximumMs={policy.maxMs}
              maximumBytes={song.maxBytes}
              onFile={next => void choose(next)}
            />
          )}
          {shown && (
            <p className="cv-meta">
              {shown.file.name} · <span className="mono">{shown.ms !== null ? mmss(shown.ms) : '—'}</span> ·{' '}
              {uiFormat('{0} МБ', (shown.file.size / 1024 / 1024).toFixed(1).replace('.', ','))}
            </p>
          )}
          {(shown || state.uploadIds[song.key] || direct.pending) && (
            <div className="bcu-acts">
              <button
                type="button"
                className="bcu-pl"
                disabled={locked}
                onClick={() => {
                  clearSong();
                  setLocal(null);
                }}
              >
                {uiText('Убрать')}
              </button>
              {!uploaded && (state.uploadIds[song.key] || direct.pending) && (
                <button
                  type="button"
                  className="bcu-pl"
                  disabled={locked || direct.busy}
                  onClick={async () => {
                    if (!song.screen) return void state.upload(song.key, null);
                    const id = await direct.check();
                    if (id) set({ song_upload_id: id });
                  }}
                >
                  {uiText('Проверить загрузку')}
                </button>
              )}
            </div>
          )}
          {sourceId && (
            <div className="bcu-acts">
              <p>{uiFormat('Из «Моих треков»: {0}', mineTitle || `№${sourceId}`)}</p>
              <button type="button" className="bcu-pl" disabled={locked} onClick={() => set({ source_order_id: null })}>
                {uiText('Убрать')}
              </button>
            </div>
          )}
        </div>
        {songError && <p role="alert">{uiText(songError)}</p>}
        {!shown && !sourceId && info('source_order_id') && (
          <button
            type="button"
            className="lnk"
            disabled={locked}
            onClick={() => {
              setMine({ open: true, items: null, error: false });
              void endpoints.history
                .list({ tab: 'audio', q: '', sort: 'new' })
                .then(page => setMine({ open: true, items: page.items.filter(item => item.status === 'done'), error: false }))
                .catch(() => setMine({ open: true, items: [], error: true }));
            }}
          >
            {uiText('Выбрать из «Моих треков»')}
          </button>
        )}
        {shown && songMs !== null && (
          <div className="cv-frag">
            <audio ref={audio} src={url} preload="metadata" />
            <button type="button" className="iconb" aria-label={uiText('Послушать')} onClick={listen}>
              <StudioIcon kind="play" />
            </button>
            {duration?.id === 'full' || songMs <= windowMs ? (
              <span>{uiText('Вся песня')}</span>
            ) : (
              <label>
                <span>{uiFormat('Фрагмент с {0} до {1}', mmss(Math.min(fromMs, maxFrom)), mmss(Math.min(fromMs, maxFrom) + windowMs))}</span>
                {info('from_ms') && (
                  <input
                    type="range"
                    min={0}
                    max={maxFrom}
                    step={1000}
                    value={Math.min(fromMs, maxFrom)}
                    disabled={locked}
                    aria-label={uiText('Начало фрагмента')}
                    onChange={event => set({ from_ms: Number(event.target.value) || null })}
                  />
                )}
                <small>{uiText('Двигайте окно, чтобы выбрать минуту')}</small>
              </label>
            )}
          </div>
        )}
      </div>
      {policy.styles.length + (policy.ownStyle ? 1 : 0) > 0 && (
        <CoverStyles
          styles={policy.styles}
          themes={policy.themes}
          value={styleId}
          own={own}
          ownAvailable={policy.ownStyle}
          disabled={locked}
          onPick={id => (id === OWN_STYLE ? set({ own_style: true, style_id: null, style_text: null }) : set({ style_id: id, own_style: null }))}
        />
      )}
      {own && textInfo && (
        <div className="cv-own">
          {textField('Опишите стиль')}
          <p className="hint">{uiText('Словами, без имён артистов')}</p>
        </div>
      )}
      {slots.length > 0 && (
        <StudioSettings slots={slots} disabled={locked} oneRow>
          {info('voice_mode') && (
            <StudioSettingsSection id="voice" title="Голос" kind="voice">
              <div className="sc-rows" role="radiogroup" aria-label={uiText('Голос')}>
                <button type="button" role="radio" aria-checked={voiceMode === 'src'} className={`sc-r${voiceMode === 'src' ? ' on' : ''}`} disabled={locked} onClick={() => set({ voice_mode: 'src', voice_id: null })}>
                  <span className="m">
                    <b>{uiText('Оригинал')}</b>
                    <small>{uiText('Голос исполнителя без изменений')}</small>
                  </span>
                </button>
                {clone && info('voice_id') && (
                  <button
                    type="button"
                    role="radio"
                    aria-checked={voiceMode === 'voice' && readText(info('voice_id'), inputs) === String(clone.voice_id)}
                    className="sc-r"
                    disabled={locked}
                    onClick={() => set({ voice_mode: 'voice', voice_id: String(clone.voice_id) })}
                  >
                    <span className="m">
                      <b>{clone.name}</b>
                      <small>{uiText('Мой голос · клон')}</small>
                    </span>
                  </button>
                )}
                {info('voice_id') && (
                  <VoicePick
                    s2s
                    value={voiceMode === 'voice' ? state.voice : null}
                    disabled={locked}
                    sub={uiText('Другой голос · Голоса с переозвучкой, подборки')}
                    onPick={voice => {
                      state.setVoice(voice);
                      set({ voice_mode: 'voice', voice_id: String(voice.id) });
                    }}
                  />
                )}
              </div>
            </StudioSettingsSection>
          )}
          {info('duration') && policy.durations.length > 0 && (
            <StudioSettingsSection id="duration" title="Длительность">
              <div className="sc-rows" role="radiogroup" aria-label={uiText('Длительность')}>
                {policy.durations.map(d => {
                  // Сумма заказа при этом выборе: у текущего — state.price, у другого — цена формы под него.
                  const sent = d.id === duration?.id ? state.price : studioExpectedPrice(form, patchSpec(form, inputs, { duration: d.id }));
                  const confirmed = form.chargeable === false || d.price === null || d.price === sent;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      role="radio"
                      aria-checked={duration?.id === d.id}
                      className={`sc-r${duration?.id === d.id ? ' on' : ''}`}
                      disabled={locked}
                      onClick={() => set({ duration: d.id, from_ms: d.id === 'full' ? null : fromMs || null })}
                    >
                      <span className="m">
                        <b>{uiText(d.title)}</b>
                        <small>
                          {d.price !== null ? uiFormat(d.id === 'full' ? '{0} C за песню' : '{0} C за фрагмент', d.price) : ''}
                          {!confirmed ? ` · ${uiText('цена уточняется')}` : ''}
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </StudioSettingsSection>
          )}
          {textInfo && !own && (
            <StudioSettingsSection id="wish" title="Пожелания к стилю" hint="необязательно" kind="text">
              {textField('Пожелания к стилю')}
              <p className="hint">{uiText('Необязательно · без имён артистов')}</p>
            </StudioSettingsSection>
          )}
        </StudioSettings>
      )}
      {form.chargeable === false && <p>{uiText('Без списания')}</p>}
      <StudioCreateButton
        kind="video"
        label="Создать кавер"
        price={price}
        busy={state.busy}
        blocked={state.submissionBlocked}
        pending={!!state.intent}
        missing={blocker}
        available={form.available}
        onCreate={() => void state.submit()}
      />
      {editor && textInfo && (
        <TextEditor
          label={uiText(own ? 'Опишите стиль' : 'Пожелания к стилю')}
          value={styleText}
          limit={policy.styleMax}
          disabled={locked}
          onChange={value => set({ style_text: value || null })}
          onClose={() => setEditor(false)}
          toolbar={
            chips.length ? (
              <div className="chips">
                {chips.map(chip => (
                  <PromptChip key={chip} label={chip} selected={styleText.includes(chip)} onClick={() => toggleChip(chip)} />
                ))}
              </div>
            ) : undefined
          }
        />
      )}
      {mine.open && (
        <StudioSheet title="Мои треки" onClose={() => setMine({ open: false, items: null, error: false })}>
          <h2>{uiText('Мои треки')}</h2>
          <p className="studio-settings-sub">{uiText('Каверы и аудио из «Истории»')}</p>
          {mine.items === null && <p role="status">{uiText('Загружаем…')}</p>}
          {mine.error && <p role="alert">{uiText('Не удалось загрузить «Мои треки».')}</p>}
          {mine.items?.length === 0 && !mine.error && <p role="status">{uiText('Пока нет готовых треков')}</p>}
          <div className="sc-rows">
            {mine.items?.map(item => (
              <button
                key={item.id}
                type="button"
                className="sc-r"
                onClick={() => {
                  clearSong();
                  setLocal(null);
                  setMineTitle(item.title ?? '');
                  set({ source_order_id: item.id });
                  setMine({ open: false, items: null, error: false });
                }}
              >
                <span className="m">
                  <b>{item.title ?? uiFormat('Трек №{0}', item.id)}</b>
                  <small>
                    {[item.kind === 'cover' ? uiText('Кавер') : uiText('Своя запись'), item.result?.duration_ms ? mmss(item.result.duration_ms) : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
              </button>
            ))}
          </div>
        </StudioSheet>
      )}
    </form>
  );
}
